import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalCount, 
  pageSize, 
  onPageChange, 
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showSizeChanger = true,
  showQuickJumper = true,
  showTotal = true 
}) => {
  const { t } = useTranslation();
  const [jumpPage, setJumpPage] = useState('');

  const handleJumpToPage = () => {
    const page = parseInt(jumpPage, 10);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setJumpPage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };

  const getVisiblePages = () => {
    if (totalPages <= 1) {
      return [1];
    }

    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    // 去除重复的页码
    return [...new Set(rangeWithDots)];
  };

  return (
    <div className="pagination">
      {showTotal && (
        <div className="pagination__group">
          <span>
            {t('pagination.showing')} {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} {t('pagination.of')} {totalCount} {t('pagination.items')}
          </span>
          {showSizeChanger && (
            <div className="pagination__group">
              <span>{t('pagination.pageSize')}:</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="theme-select"
                style={{ minHeight: '2.6rem', width: 'auto', padding: '0.5rem 0.9rem' }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="pagination__group">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination__button"
        >
          ‹ {t('pagination.previous')}
        </button>

        <div className="pagination__group">
          {totalPages > 1 && getVisiblePages().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="floating-note">
                  ...
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPageChange(page)}
                  className={`pagination__button ${page === currentPage ? 'is-active' : ''}`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination__button"
        >
          {t('pagination.next')} ›
        </button>

        {showQuickJumper && totalPages > 1 && (
          <div className="pagination__group">
            <span>{t('pagination.jumpTo')}</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={currentPage.toString()}
              className="theme-input"
              style={{ width: '4.4rem', minHeight: '2.6rem', padding: '0.5rem 0.8rem', textAlign: 'center' }}
            />
            <button
              type="button"
              onClick={handleJumpToPage}
              className="pagination__button is-active"
            >
              {t('pagination.go')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination; 
